import { useState } from 'react'
import { Button, Skeleton } from '@heroui/react'

import { ErrorState } from '@/components/ui/data-state'

import { ShiftReportType } from '../types'
import type { PosSession } from '../types'
import { useXReport, useZReport } from '../hooks'
import { ShiftReportView } from './shift-report-view'

interface ShiftReportPanelProps {
  session: PosSession
}

/**
 * One drawer's reports, switchable — the whole X/Z surface in one component.
 *
 * Shared by the two places a report is read: the Reports tab, which picks a
 * drawer and shows it on the page, and the Sessions row modal, which arrives at
 * the same drawer from the list. They are the same question asked from two
 * directions, and two implementations would drift — the modal would keep the
 * device disclosure the tab lost, or the tab would default to a Z the modal knew
 * was missing.
 *
 * Only the view being looked at is fetched. An X costs the server a real
 * aggregate over the period's orders, so eagerly loading both would spend two
 * queries per click to fill a tab nobody opened.
 */
export function ShiftReportPanel({ session }: ShiftReportPanelProps) {
  const { view, show } = useShiftReportView(session)
  const isViewingX = view === ShiftReportType.X

  const {
    xReport,
    isLoading: isLoadingX,
    isRefreshing,
    error: xError,
    retry: retryX,
  } = useXReport(isViewingX ? session.id : null, session.isOpen)

  const {
    zReport,
    isLoading: isLoadingZ,
    error: zError,
    retry: retryZ,
  } = useZReport(isViewingX ? null : session.id)

  return (
    <div className="flex flex-col gap-5">
      <ShiftReportSwitch
        value={view}
        onChange={show}
        isLive={session.isOpen}
        hasZ={!session.isOpen && session.status === 'closed'}
      />

      {isViewingX ? (
        isLoadingX ? (
          <Skeleton className="h-96 w-full rounded-2xl" />
        ) : xError ? (
          <ErrorState subject="the X report" error={xError} onRetry={retryX} />
        ) : xReport ? (
          <ShiftReportView report={xReport.report} isRefreshing={isRefreshing} />
        ) : null
      ) : isLoadingZ ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : zError ? (
        <ErrorState subject="the Z report" error={zError} onRetry={retryZ} />
      ) : zReport?.report ? (
        <>
          <ShiftReportView report={zReport.report} />

          {/* The till's own copy of the same close, when it sent one. Collapsed,
              and raw: it rides along beside the server's document rather than
              replacing it, so a disagreement is something a person can look at
              later — but it is not the record, and laying it out like one would
              imply that it was. */}
          {zReport.device && (
            <details className="border-border rounded-xl border px-3 py-2">
              <summary className="cursor-pointer text-xs font-medium">
                What the till itself reported
              </summary>
              <p className="text-muted mt-1 mb-2 text-[0.7rem]">
                Kept beside the server&rsquo;s figures to be compared, not trusted over them. The
                report above is the record.
              </p>
              <pre className="border-border bg-background max-h-56 overflow-auto rounded-lg border p-3 text-[0.7rem]">
                {JSON.stringify(zReport.device, null, 2)}
              </pre>
            </details>
          )}
        </>
      ) : (
        <div className="flex flex-col items-start gap-2">
          <p className="text-muted text-xs">
            {session.isOpen
              ? 'Not yet — a Z is written when the drawer is counted at the till. Until then the X is the reading to take.'
              : 'This period ended without the till ever reporting its totals, so no Z was frozen.'}
          </p>
          <Button variant="ghost" size="sm" onPress={() => show(ShiftReportType.X)}>
            Take an X instead
          </Button>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Which one is showing                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The visible report, and the rule for changing it.
 *
 * The default belongs to the DRAWER, not to the component's lifetime: both
 * callers keep this mounted and hand it a different session as the picker or the
 * list moves, so a bare `view` state would carry the last drawer's tab onto the
 * next one and open a live session on a Z it does not have.
 *
 * Solved by stamping the choice with the session it was made on, so the default
 * stays DERIVED. A choice that does not belong to the drawer in front of you
 * simply does not apply — no effect, and therefore no render where the wrong
 * report is briefly on screen.
 */
function useShiftReportView(session: PosSession) {
  const [choice, setChoice] = useState<{ sessionId: string; type: ShiftReportType } | null>(null)

  const view =
    choice && choice.sessionId === session.id
      ? choice.type
      : session.isOpen
        ? ShiftReportType.X
        : ShiftReportType.Z

  return {
    view,
    show: (type: ShiftReportType) => setChoice({ sessionId: session.id, type }),
  }
}

/**
 * Z or X, with what each one means written underneath.
 *
 * Both options are always offered, including the Z on a drawer that has none:
 * hiding it would leave a manager unsure whether this session simply has no Z or
 * whether the screen forgot to offer one, and "there is no Z, and here is why" is
 * the more useful answer. The X is never disabled — it is a pure read and always
 * computable, which is exactly what makes it the fallback.
 */
function ShiftReportSwitch({
  value,
  onChange,
  isLive,
  hasZ,
}: {
  value: ShiftReportType
  onChange: (value: ShiftReportType) => void
  isLive: boolean
  hasZ: boolean
}) {
  const options = [
    { id: ShiftReportType.Z, label: hasZ ? 'Z report' : 'Z report · none' },
    { id: ShiftReportType.X, label: isLive ? 'X report · live' : 'X report · re-read' },
  ]

  return (
    <div className="flex flex-col gap-1.5">
      <div className="border-border bg-surface inline-flex w-fit gap-1 rounded-full border p-1">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={value === option.id}
            onClick={() => onChange(option.id)}
            className={`rounded-full px-3 py-1 text-xs transition-colors ${
              value === option.id
                ? 'bg-background text-foreground font-medium shadow-sm'
                : 'text-muted hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <p className="text-muted text-[0.7rem]">
        {value === ShiftReportType.Z
          ? 'The document of record, frozen when the drawer was counted. It will read the same in six months.'
          : isLive
            ? 'Where the drawer stands right now, recomputed every few seconds. Taking one changes nothing.'
            : 'The same period, recomputed from the orders as they stand now. Compare it with the Z — anything booked after the close shows up here only.'}
      </p>
    </div>
  )
}
